import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { Folder, MoreVertical, Trash2, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import RenameFolderDialog from '@/components/RenameFolderDialog';

interface Folder {
    id: number;
    name: string;
    parent_id: number | null;
    created_at: string;
    files_count: number;
    subfolders_count: number;
}

interface FolderListProps {
    folders: Folder[];
    onDrop: (folderId: number | null) => void;
    isDragging: boolean;
}

export default function FolderList({ folders, onDrop, isDragging }: FolderListProps) {
    const [renamingFolder, setRenamingFolder] = useState<Folder | null>(null);
    const [dragOverId, setDragOverId] = useState<number | null>(null);

    const handleDelete = (folder: Folder) => {
        if (confirm(`Are you sure you want to delete "${folder.name}"?`)) {
            router.delete(`/folders/${folder.id}`);
        }
    };

    const handleOpen = (folder: Folder) => {
        router.visit(`/folders/${folder.id}`);
    };

    const handleDragOver = (e: React.DragEvent, folderId: number) => {
        if (isDragging) {
            e.preventDefault();
            e.stopPropagation();
            setDragOverId(folderId);
        }
    };

    const handleDragLeave = () => {
        setDragOverId(null);
    };

    const handleDrop = (e: React.DragEvent, folderId: number) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverId(null);
        onDrop(folderId);
    };

    return (
        <>
            <div>
                <h2 className="text-sm font-semibold text-muted-foreground mb-3">Folders</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {folders.map((folder) => (
                        <div
                            key={folder.id}
                            className={`
                group relative border rounded-lg p-4 cursor-pointer transition-colors
                ${dragOverId === folder.id
                                    ? 'bg-primary/20 border-primary border-2'
                                    : 'hover:bg-accent'
                                }
              `}
                            onClick={() => !isDragging && handleOpen(folder)}
                            onDragOver={(e) => handleDragOver(e, folder.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, folder.id)}
                        >
                            <div className="flex items-start justify-between">
                                <Folder className="h-8 w-8 text-primary" />
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 opacity-0 group-hover:opacity-100"
                                        >
                                            <MoreVertical className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setRenamingFolder(folder);
                                            }}
                                        >
                                            <Edit2 className="h-4 w-4 mr-2" />
                                            Rename
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(folder);
                                            }}
                                            className="text-destructive"
                                        >
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            Delete
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                            <div className="mt-2">
                                <p className="font-medium text-sm truncate">{folder.name}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {folder.files_count} files + {folder.subfolders_count} folders
                                </p>
                            </div>
                            {dragOverId === folder.id && (
                                <div className="absolute inset-0 flex items-center justify-center bg-primary/10 rounded-lg">
                                    <p className="text-sm font-medium text-primary">Drop here</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {renamingFolder && (
                <RenameFolderDialog
                    folder={renamingFolder}
                    onClose={() => setRenamingFolder(null)}
                />
            )}
        </>
    );
}